import type AVPlayer from '@libmedia/avplayer';
import type { Language } from '@/types/model';
import { trackLanguage } from './vodTracks';

export type VodEngine = AVPlayer;
export interface VodTrack {
  id: number;
  language?: Language;
  label: string;
}

export const loadVodEngine = async (): Promise<typeof AVPlayer> => {
  // Preserve the player's own module URLs so its workers and dynamic chunks resolve correctly.
  const url = new URL(`${import.meta.env.BASE_URL}media-player/avplayer.js`, window.location.href).href;
  return (await import(/* @vite-ignore */ url)).default;
};

export const getVodTracks = (player: VodEngine, type: 'Audio' | 'Subtitle', declared: Language[]): VodTrack[] => {
  const streams = player.getStreams().filter((stream) => stream.mediaType === type);
  const tracks = streams.map((stream) => ({
    id: Number(stream.id),
    language: trackLanguage({
      language: String(stream.metadata.language ?? ''),
      label: String(stream.metadata.title ?? stream.metadata.name ?? ''),
    }),
    label: String(stream.metadata.title ?? stream.metadata.name ?? ''),
  }));
  if (tracks.length === declared.length && tracks.every((track) => !track.language)) {
    tracks.forEach((track, index) => { track.language = declared[index]; });
  }
  return tracks;
};

export const selectVodSubtitle = async (player: VodEngine, id: number | null) => {
  if (id === null) {
    player.setSubtitleEnable(false);
    return;
  }
  const time = player.currentTime;
  const changed = Number(player.getSelectedSubtitleStreamId()) !== id;
  player.setSubtitleEnable(false);
  if (changed) {
    await player.selectSubtitle(id);
  }
  player.setSubtitleEnable(true);
  // Re-read buffered cues for the selected stream, without restarting the movie.
  if (changed) await player.seek(time);
};

// MSE's default duration only covers buffered data. Set the file duration so the
// browser's native controls can seek throughout the movie, including unbuffered parts.
export const setVodNativeDuration = async (player: VodEngine) => {
  const Player = await loadVodEngine();
  const source = await Player.MSEThread.getMediaSource(player.taskId);
  const duration = Number(player.getDuration()) / 1000;
  const update = () => {
    if (source.readyState !== 'open' || !Number.isFinite(duration) || duration <= 0) return;
    const buffers = Array.from(source.sourceBuffers);
    if (buffers.some((buffer) => buffer.updating)) {
      buffers.filter((buffer) => buffer.updating).forEach((buffer) => buffer.addEventListener('updateend', update, { once: true }));
      return;
    }
    if (source.duration !== duration) source.duration = duration;
  };
  update();
};

export const seekVodNative = async (player: VodEngine, video: HTMLVideoElement, position: bigint) => {
  const paused = video.paused;
  await player.seek(position);
  // The demuxer may land just after the requested time at a keyframe.
  const seconds = Math.max(Number(position) / 1000, video.currentTime);
  const buffered = () => Array.from({ length: video.buffered.length }, (_, index) => index)
    .some((index) => video.buffered.start(index) <= seconds && video.buffered.end(index) >= seconds);
  // A long GOP can put the requested frame beyond the engine's initial seek
  // buffer. Fill that range even while paused, then position the native video.
  if (!buffered()) {
    const Player = await loadVodEngine();
    await Player.MSEThread.setCurrentTime(player.taskId, seconds);
    await Player.MSEThread.unpause(player.taskId);
    try {
      await new Promise<void>((resolve, reject) => {
        const started = performance.now();
        const timer = window.setInterval(() => {
          if (buffered()) { window.clearInterval(timer); resolve(); }
          else if (!video.isConnected || performance.now() - started > 15000) {
            window.clearInterval(timer);
            reject(new Error('Media seek could not be buffered'));
          }
        }, 50);
      });
    } finally {
      if (paused) await Player.MSEThread.pause(player.taskId);
    }
  }
  video.currentTime = seconds;
};
